import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  model,
  output,
  viewChild,
  ViewEncapsulation,
  AfterViewInit,
  OnDestroy,
  computed,
  HostListener,
  effect,
} from '@angular/core';

import { ConversationFooterComponent } from '../conversation-footer/conversation-footer.component';
import { ChatMessagesComponent } from '../chat-messages/chat-messages.component';
import {
  FooterButtonType,
  HeaderButtonType,
  MessageButtonClickEvent,
  MessageSenderType,
} from '../../../interfaces';
import { FormControl } from '@angular/forms';
import { Message, Participant, ParticipantKind, MessageTemplateLight } from '@clientwire/api';
import { logger } from '../../../utils';
import { FilesToUpload } from '@akostm/orbit-ui';
import { ErrorState, ParticipantWithSourceId } from '../../../stores';
import { WindowService } from '../../../services';

@Component({
  selector: 'shared-conversation',
  standalone: true,
  imports: [ConversationFooterComponent, ChatMessagesComponent],
  templateUrl: './conversation.component.html',
  styleUrl: './conversation.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationComponent implements AfterViewInit, OnDestroy {
  defaultController = new FormControl<string | null>(null);
  #isOpenLiveTracker: number | null = null;
  #resizeObserver?: ResizeObserver;
  #resizeTimeout?: number;
  readonly messageSenderTypeEnum = MessageSenderType;
  readonly footerButtonTypeEnum = FooterButtonType;

  //#region Inputs
  readonly conversationId = input.required<string>();
  readonly templates = input<Map<string, MessageTemplateLight> | undefined>();
  readonly messages = input.required<Message[]>();
  readonly senderType = input.required<MessageSenderType>();
  readonly isConversationLocked = input(false);
  readonly conversationLockNote = input<string>('SHARED.CONVERSATION_LOCKED.DEFAULT');
  readonly isConversationArchived = input(false);
  readonly conversationData = input<object>();
  readonly participant = input<Participant>();
  readonly nextMessageSourceId = input<string>();
  readonly errors = input<ErrorState[]>();
  readonly messageInputController = input<FormControl | undefined>(this.defaultController);
  readonly loading = input<boolean>(false);
  readonly messagePrependLoading = input<boolean>(false);
  readonly messageAppendLoading = input<boolean>(false);
  readonly otherTypingParticipants = input<ParticipantWithSourceId[]>();
  readonly conversationParticipants = input<Participant[]>();
  readonly messageIdsWithError = input<string[]>();
  readonly userId = input<string | null>();
  readonly windowService = input.required<WindowService>();
  readonly userIsNotParticipant = input<boolean>();
  readonly conversationAppType = input.required<'main' | 'embed' | 'client'>();
  readonly sharedUserDisplayName = input<string | undefined | null>();
  //#endregion

  //#region Outputs
  readonly unlockAction = output<string>();
  readonly sendMessage = output<FooterButtonType>();
  readonly scrolled = output<void>();
  readonly scrolledUp = output<void>();
  readonly messageRead = output<Message>();
  readonly scrolledToBottom = output<boolean>();
  readonly messagesLimitChanged = output<HTMLElement>();
  readonly assetClicked = output<string>();
  readonly messageButtonClicked = output<MessageButtonClickEvent>();
  readonly sendOpenLiveTrackerEvent = output<boolean>();
  readonly refreshMessages = output<void>();
  readonly joinConversation = output<void>();
  readonly resendMessage = output<string>();
  readonly isTyping = output<boolean>();
  readonly fetchForNewMessages = output<void>();
  //#endregion

  //#region Models
  readonly files = model<FilesToUpload[]>();
  //#endregion

  //#region ViewChildren
  readonly conversationMessages = viewChild('conversationMessages', { read: ElementRef });
  readonly chatMessages = viewChild(ChatMessagesComponent);
  readonly chatMessagesRef = viewChild(ChatMessagesComponent, { read: ElementRef });
  readonly conversationFooter = viewChild(ConversationFooterComponent);
  //#endregion

  //#region Computed
  readonly noUsers = computed(() => {
    return !this.conversationParticipants()?.some(
      (participant) => participant.kind === ParticipantKind.User
    );
  });
  //#endregion

  @HostListener('document:visibilitychange', ['$event'])
  appVisibility() {
    if (!document.hidden) {
      this.fetchForNewMessages.emit();
    }
  }

  constructor() {
    effect(() => {
      // This effect will run when the view is initialized and the element is available
      const messagesElement = this.conversationMessages();
      if (messagesElement?.nativeElement) {
        this.#setupResizeObserver(messagesElement.nativeElement);
      }
    });
  }

  ngAfterViewInit(): void {
    this.#startIsOpenLiveTracker();
  }

  ngOnDestroy(): void {
    this.#stopIsOpenLiveTracker();
    this.#resizeObserver?.disconnect();
    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
    }
  }

  handleHeaderButtonClick(event: HeaderButtonType) {
    logger.debug('handleButtonClick', event);
  }

  #startIsOpenLiveTracker() {
    this.sendOpenLiveTrackerEvent.emit(true);
    this.#isOpenLiveTracker = window.setInterval(() => {
      this.sendOpenLiveTrackerEvent.emit(true);
    }, 10000);
  }

  #stopIsOpenLiveTracker() {
    this.sendOpenLiveTrackerEvent.emit(false);
    if (this.#isOpenLiveTracker !== null) {
      clearInterval(this.#isOpenLiveTracker);
    }
  }

  sendIsTypingEvent(isTyping: boolean) {
    if (isTyping) {
      this.isTyping.emit(true);
    }
  }

  #setupResizeObserver(element: HTMLElement): void {
    // Clean up any existing observer
    this.#resizeObserver?.disconnect();

    this.#resizeObserver = new ResizeObserver(() => {
      // Debounce the resize events
      if (this.#resizeTimeout) {
        clearTimeout(this.#resizeTimeout);
      }

      this.#resizeTimeout = window.setTimeout(() => {
        this.messagesLimitChanged.emit(element);
      }, 300);
    });

    this.#resizeObserver.observe(element);
  }
}
